import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Building } from "lucide-react";
import { useAgencyMembers } from "@/hooks/platform/useAgency";
import { LoadingState, ErrorState, EmptyState } from "@/components/dashboard/common/PageShell";

const VenueRoster = () => {
  const { data: venues = [], isLoading, error, refetch } = useAgencyMembers("venue");
  const [search, setSearch] = useState("");

  const rows = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return venues;
    return venues.filter((v) =>
      [v.business_name, v.username, v.full_name, v.portal_id, v.city].some((x) => (x ?? "").toLowerCase().includes(t)),
    );
  }, [venues, search]);

  if (isLoading) return <LoadingState label="Loading venue roster…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-400">{venues.length} venues under oversight</p>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search venue or portal ID"
          className="w-64 bg-zinc-950 border-zinc-800 text-white"
        />
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={Building} title="No venues yet" description="Venues appear here once they register." />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((v) => (
            <Card key={v.id} className="bg-white/5 border-white/20">
              <CardContent className="p-4 flex gap-3">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={v.avatar_url ?? undefined} />
                  <AvatarFallback>{(v.business_name || v.username).slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-white truncate">{v.business_name || v.full_name || v.username}</p>
                  <p className="text-xs text-gray-400 truncate">{[v.city, v.country].filter(Boolean).join(", ") || "—"}</p>
                  <p className="text-[11px] font-mono text-gray-500 mt-1">{v.portal_id ?? "—"}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <Badge className="bg-white/10 border-0 text-xs capitalize">
                      {(v.verification_status ?? "unverified").replace("_", " ")}
                    </Badge>
                    {v.business_registration && <Badge className="bg-white/10 border-0 text-xs">Reg {v.business_registration}</Badge>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default VenueRoster;
